import { useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Pencil, UserX, UserCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { AdminUser, UserRole } from '@/types/adminUser'

ModuleRegistry.registerModules([AllCommunityModule])

const ROLE_COLORS: Record<UserRole, { bg: string; text: string }> = {
  ADMIN: { bg: 'bg-red-100', text: 'text-red-700' },
  MANAGER: { bg: 'bg-blue-100', text: 'text-blue-700' },
  MERCHANT: { bg: 'bg-purple-100', text: 'text-purple-700' },
}

function TextCell(props: CustomCellRendererProps<AdminUser>) {
  return (
    <span className="text-sm text-foreground line-clamp-2">
      {props.value as string}
    </span>
  )
}

function DisplayNameCell(props: CustomCellRendererProps<AdminUser>) {
  const user = props.data
  if (!user) return null

  return (
    <span className="text-sm text-foreground line-clamp-2">
      {user.display_name ?? user.email}
    </span>
  )
}

const DEFAULT_ROLE_COLOR = { bg: 'bg-gray-100', text: 'text-gray-700' }

function RoleBadgeCell(props: CustomCellRendererProps<AdminUser>) {
  const role = props.value as UserRole
  if (!role) return null

  const colors = ROLE_COLORS[role] ?? DEFAULT_ROLE_COLOR

  return (
    <Badge
      variant="outline"
      className={cn('border-transparent font-medium', colors.bg, colors.text)}
    >
      {role}
    </Badge>
  )
}

function StatusCell(props: CustomCellRendererProps<AdminUser>) {
  const user = props.data
  if (!user) return null

  const isActive = user.is_active

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'h-2 w-2 rounded-full',
          isActive ? 'bg-green-500' : 'bg-gray-400'
        )}
      />
      <span className="text-sm">{isActive ? 'Active' : 'Inactive'}</span>
    </div>
  )
}

interface ActionsCellProps extends CustomCellRendererProps<AdminUser> {
  onEditRole: (user: AdminUser) => void
  onToggleStatus: (user: AdminUser) => void
}

function ActionsCell(props: ActionsCellProps) {
  const user = props.data
  if (!user) return null

  const { onEditRole, onToggleStatus } = props
  const isActive = user.is_active

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        onClick={() => onEditRole(user)}
      >
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        onClick={() => onToggleStatus(user)}
      >
        {isActive ? (
          <UserX className="h-4 w-4 text-muted-foreground" />
        ) : (
          <UserCheck className="h-4 w-4 text-muted-foreground" />
        )}
      </Button>
    </div>
  )
}

interface AdminUsersGridProps {
  users: AdminUser[]
  onEditRole: (user: AdminUser) => void
  onToggleStatus: (user: AdminUser) => void
  loading?: boolean
}

export function AdminUsersGrid({
  users,
  onEditRole,
  onToggleStatus,
  loading,
}: AdminUsersGridProps) {
  const columnDefs: ColDef<AdminUser>[] = useMemo(
    () => [
      {
        field: 'display_name',
        headerName: 'Name',
        flex: 2,
        minWidth: 150,
        cellRenderer: DisplayNameCell,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 1,
        minWidth: 100,
        maxWidth: 140,
        cellRenderer: RoleBadgeCell,
      },
      {
        field: 'is_active',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: StatusCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 100,
        maxWidth: 120,
        cellClass: 'ag-center-aligned-cell',
        headerClass: 'ag-center-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onEditRole,
          onToggleStatus,
        },
      },
    ],
    [onEditRole, onToggleStatus]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<AdminUser>
            rowData={users}
            columnDefs={columnDefs}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            loading={loading}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            overlayNoRowsTemplate="<span>No users found</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
          />
        </div>
      </div>
    </div>
  )
}
